import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Trash2, AlertTriangle, RefreshCw, Package, Tag, Boxes, CheckCircle, XCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FOBEntryManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Entry list
  const [entries, setEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [listSearchTerm, setListSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const limit = 20;
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteEntryId, setDeleteEntryId] = useState('');
  const [selectedOperations, setSelectedOperations] = useState({
    op1: true,
    op2: true,
    op3: true,
    op4: true
  });
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchEntries = async () => {
    setIsLoadingEntries(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (listSearchTerm) params.append('search', listSearchTerm);
      
      const response = await apiClient.get(`/admin/fob-entries/list?${params}`);
      setEntries(response.data.entries || []);
      setTotalEntries(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load FOB entries');
    } finally {
      setIsLoadingEntries(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter an entry ID to search');
      return;
    }
    
    setIsSearching(true);
    setSearchResults(null);
    try {
      const response = await apiClient.get(`/admin/fob-entries/search?entry_id=${encodeURIComponent(searchTerm.trim())}`);
      setSearchResults(response.data);
      
      if (!response.data.found) {
        toast.info('No FOB operations found for this entry ID');
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const openDeleteDialog = (entryId) => {
    setDeleteEntryId(entryId);
    setSelectedOperations({ op1: true, op2: true, op3: true, op4: true });
    setConfirmText('');
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    const opsToDelete = Object.entries(selectedOperations)
      .filter(([_, selected]) => selected)
      .map(([op]) => op);
    
    if (opsToDelete.length === 0) {
      toast.error('Please select at least one operation to delete');
      return;
    }
    
    setIsDeleting(true);
    try {
      const operations = opsToDelete.length === 4 ? 'all' : opsToDelete.join(',');
      const response = await apiClient.delete(
        `/admin/fob-entries/${encodeURIComponent(deleteEntryId)}?operations=${operations}`
      );
      
      toast.success(`Deleted ${response.data.total_records_deleted} records for ${deleteEntryId}`);
      setShowDeleteDialog(false);
      setSearchResults(null);
      setSearchTerm('');
      fetchEntries();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleListSearch = () => {
    setPage(0);
    fetchEntries();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'completed': 'bg-green-100 text-green-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'usn_assigned': 'bg-blue-100 text-blue-800',
      'bundles_complete': 'bg-purple-100 text-purple-800',
      'not_started': 'bg-gray-100 text-gray-600'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">FOB Entry Management</h1>
              <p className="text-sm text-[#E8DCC8]">Delete FOB operations data by entry number</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* Search by Entry ID */}
        <Card className="border-[#E5DFD0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1A1F2E]">
              <Search className="w-5 h-5" />
              Search Entry by ID
            </CardTitle>
            <CardDescription>
              Enter an entry ID (document barcode) to view and delete its FOB operations data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="Enter Entry ID (e.g., DOC000001/25-26)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
                data-testid="search-entry-input"
              />
              <Button 
                onClick={handleSearch} 
                disabled={isSearching}
                className="bg-[#2D5F4F] hover:bg-[#234a3d]"
                data-testid="search-entry-btn"
              >
                {isSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                <span className="ml-2">Search</span>
              </Button>
            </div>

            {/* Search Results */}
            {searchResults && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#1A1F2E]">
                    Results for: {searchResults.entry_id}
                  </h3>
                  {searchResults.found && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => openDeleteDialog(searchResults.entry_id)}
                      data-testid="delete-entry-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Entry Data
                    </Button>
                  )}
                </div>

                {searchResults.consignment && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Consignment:</strong> {searchResults.consignment.document_barcode} | 
                      Status: {searchResults.consignment.status} | 
                      Bundles: {searchResults.consignment.bundle_count}
                    </p>
                  </div>
                )}

                {searchResults.found ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Operation 1 */}
                    <Card className={searchResults.operations.op1_bundle_receipt ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-5 h-5 text-[#2D5F4F]" />
                          <h4 className="font-semibold">Operation 1: Bundle Receipt</h4>
                        </div>
                        {searchResults.operations.op1_bundle_receipt ? (
                          <div className="text-sm space-y-1">
                            <p>Status: <span className={`px-2 py-0.5 rounded ${getStatusBadge(searchResults.operations.op1_bundle_receipt.status)}`}>
                              {searchResults.operations.op1_bundle_receipt.status}
                            </span></p>
                            <p>Received: {searchResults.operations.op1_bundle_receipt.received_bundles} bundles</p>
                            <p>Pending: {searchResults.operations.op1_bundle_receipt.pending_bundles} bundles</p>
                            <p className="text-gray-500">Started by: {searchResults.operations.op1_bundle_receipt.started_by}</p>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Operation 2 */}
                    <Card className={searchResults.operations.op2_usn_tagging ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Tag className="w-5 h-5 text-[#2D5F4F]" />
                          <h4 className="font-semibold">Operation 2: USN Tagging</h4>
                        </div>
                        {searchResults.operations.op2_usn_tagging ? (
                          <div className="text-sm space-y-1">
                            <p>Status: <span className={`px-2 py-0.5 rounded ${getStatusBadge(searchResults.operations.op2_usn_tagging.status)}`}>
                              {searchResults.operations.op2_usn_tagging.status}
                            </span></p>
                            <p>Bundles Processed: {searchResults.operations.op2_usn_tagging.bundles_processed}</p>
                            <p>Sort Numbers: {searchResults.operations.op2_usn_tagging.sort_numbers}</p>
                            <p className="text-orange-600 font-medium">USNs Assigned: {searchResults.operations.op2_usn_tagging.usns_assigned}</p>
                            <p className="text-gray-500">Created by: {searchResults.operations.op2_usn_tagging.created_by}</p>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Operation 3 */}
                    <Card className={searchResults.operations.op3_assortment ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Boxes className="w-5 h-5 text-[#2D5F4F]" />
                          <h4 className="font-semibold">Operation 3: Assortment</h4>
                        </div>
                        {searchResults.operations.op3_assortment ? (
                          <div className="text-sm space-y-1">
                            <p className="text-lg font-bold text-[#2D5F4F]">{searchResults.operations.op3_assortment.count} entries</p>
                            <p>Sample Status: {searchResults.operations.op3_assortment.sample_status}</p>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Operation 4 */}
                    <Card className={searchResults.operations.op4_packing ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-5 h-5 text-[#2D5F4F]" />
                          <h4 className="font-semibold">Operation 4: Packing</h4>
                        </div>
                        {searchResults.operations.op4_packing ? (
                          <div className="text-sm space-y-1">
                            <p className="text-lg font-bold text-[#2D5F4F]">{searchResults.operations.op4_packing.count} entries</p>
                            {searchResults.operations.op4_packing.fob_packing_count > 0 && (
                              <p>FOB Packing: {searchResults.operations.op4_packing.fob_packing_count}</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-100 rounded-lg text-center">
                    <Info className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">No FOB operations data found for this entry ID</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Entry List */}
        <Card className="border-[#E5DFD0]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-[#1A1F2E]">FOB Entries List</CardTitle>
                <CardDescription>All FOB section entries with operation status</CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchEntries}
                disabled={isLoadingEntries}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingEntries ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-4">
              <Input
                placeholder="Search by entry ID or document barcode..."
                value={listSearchTerm}
                onChange={(e) => setListSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleListSearch()}
                className="flex-1"
              />
              <Button onClick={handleListSearch} variant="outline">
                <Search className="w-4 h-4" />
              </Button>
            </div>

            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entry ID</TableHead>
                    <TableHead>Document Barcode</TableHead>
                    <TableHead className="text-center">Op1</TableHead>
                    <TableHead className="text-center">Op2</TableHead>
                    <TableHead className="text-center">Op3</TableHead>
                    <TableHead className="text-center">Op4</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingEntries ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#2D5F4F]" />
                      </TableCell>
                    </TableRow>
                  ) : entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No FOB entries found
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((entry) => (
                      <TableRow key={entry.entry_id}>
                        <TableCell className="font-mono text-sm">{entry.entry_id}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.document_barcode}</TableCell>
                        <TableCell className="text-center">
                          {entry.op1_status === 'completed' ? (
                            <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                          ) : entry.op1_status === 'in_progress' ? (
                            <RefreshCw className="w-5 h-5 text-yellow-600 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-300 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {entry.op2_status === 'completed' || entry.op2_status === 'usn_assigned' ? (
                            <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                          ) : entry.op2_status === 'in_progress' || entry.op2_status === 'bundles_complete' ? (
                            <RefreshCw className="w-5 h-5 text-yellow-600 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-300 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {entry.op3_count > 0 ? (
                            <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">{entry.op3_count}</span>
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-300 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {entry.op4_count > 0 ? (
                            <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">{entry.op4_count}</span>
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-300 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog(entry.entry_id)}
                            data-testid={`delete-btn-${entry.entry_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalEntries > limit && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-600">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, totalEntries)} of {totalEntries}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={(page + 1) * limit >= totalEntries}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Delete FOB Entry Data
            </DialogTitle>
            <DialogDescription>
              This will permanently delete FOB operations data for entry:
              <span className="font-mono font-bold block mt-1">{deleteEntryId}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> If USNs were assigned in Operation 2, they will be released back to the available pool.
              </p>
            </div>

            <div className="space-y-3">
              <Label className="font-semibold">Select operations to delete:</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="op1"
                    checked={selectedOperations.op1}
                    onCheckedChange={(checked) => setSelectedOperations(s => ({ ...s, op1: checked }))}
                  />
                  <label htmlFor="op1" className="text-sm">Operation 1: Bundle Receipt</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="op2"
                    checked={selectedOperations.op2}
                    onCheckedChange={(checked) => setSelectedOperations(s => ({ ...s, op2: checked }))}
                  />
                  <label htmlFor="op2" className="text-sm">Operation 2: USN Tagging (will release USNs)</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="op3"
                    checked={selectedOperations.op3}
                    onCheckedChange={(checked) => setSelectedOperations(s => ({ ...s, op3: checked }))}
                  />
                  <label htmlFor="op3" className="text-sm">Operation 3: Assortment</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="op4"
                    checked={selectedOperations.op4}
                    onCheckedChange={(checked) => setSelectedOperations(s => ({ ...s, op4: checked }))}
                  />
                  <label htmlFor="op4" className="text-sm">Operation 4: Packing</label>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-red-700">Type DELETE to confirm:</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="mt-1 border-red-300 focus:border-red-500"
                data-testid="confirm-delete-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || confirmText !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FOBEntryManagement;
